import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import config from 'config';
import { setAccessToken, signOutUser } from 'redux/slices/user.slice';

import { isAccessTokenExpired } from 'authTokenExpiry';
import { clearPersistedUser } from './clearPersistUser';

// Exported so the bootstrap gate (components/AuthBootstrapGate) can apply the SAME
// exclusion: these embedded -demo/-client iframes get their token via postMessage, not
// /auth/login, so they have no refresh cookie and must never trigger a refresh/signOut.
export const PATHS_WITH_SPECIAL_AUTH = [
  '/pompeyo-demo',
  '/pompeyo-client',
  '/bruno-fritsch-demo',
  '/bruno-fritsch-client',
  '/autoland-demo',
  '/autoland-client',
  '/autosrelif-demo',
  '/autosrelif-client',
];

const baseQuery = fetchBaseQuery({
  baseUrl: config.apiUrl,
  prepareHeaders: (headers, { getState }) => {
    const state = getState() as { user?: { accessToken?: string } };
    const token = state?.user?.accessToken;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

// Every Relif tab shares ONE refresh-token cookie. The last access token any tab minted is
// published here (synchronously, localStorage) so a sibling tab whose token just expired can
// adopt it instead of rotating again. See refreshAccessToken for why that matters.
export const SHARED_ACCESS_TOKEN_KEY = 'relif:lastRefreshedAccessToken';

const readSharedAccessToken = (): string | null => {
  try {
    return localStorage.getItem(SHARED_ACCESS_TOKEN_KEY);
  } catch {
    return null;
  }
};

const writeSharedAccessToken = (token: string): void => {
  try {
    localStorage.setItem(SHARED_ACCESS_TOKEN_KEY, token);
  } catch {
    // storage disabled/full — degrade to per-tab refresh, still correct just less coordinated
  }
};

const rotateThroughCookie = async (): Promise<string | null> => {
  try {
    const response = await fetch(`${config.authUrl}/auth/refresh`, {
      method: 'POST',
      // The refresh token lives in an httpOnly cookie scoped to /auth. config.authUrl is the
      // SPA's own origin in hosted envs (proxied to the API by server.js) so the cookie is
      // first-party — Safari/Firefox would otherwise drop it as third-party. The custom header
      // is the CSRF gate — the backend rejects cookie-only POSTs without it.
      credentials: 'include',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!response.ok) {
      return null;
    }
    const data = (await response.json()) as { accessToken?: string };
    return data.accessToken ?? null;
  } catch {
    return null;
  }
};

// The critical section, serialized across tabs by the Web Lock below: if a sibling tab
// already minted a still-valid token, adopt it; otherwise do the one network rotation and
// publish the result for the others.
const refreshOnce = async (staleToken?: string): Promise<string | null> => {
  const shared = readSharedAccessToken();
  if (shared && shared !== staleToken && !isAccessTokenExpired(shared)) {
    return shared;
  }
  const token = await rotateThroughCookie();
  if (token) {
    writeSharedAccessToken(token);
  }
  return token;
};

// Single-flight refresh, on TWO levels:
//  - per-tab: concurrent 401s (a page mounting several queries at once) share one round-trip
//    via refreshPromise.
//  - cross-tab: navigator.locks serializes refresh across every Relif tab sharing the cookie
//    jar. Without it, N tabs fired N concurrent POST /auth/refresh with the same cookie token;
//    the backend rotates on the first and the rest replay a just-rotated token, which once it
//    ages past the server's rotation grace trips reuse-detection and revokes the WHOLE token
//    family — logging every tab out ~each access-token TTL. With the lock only one tab rotates;
//    the others adopt the token it publishes (see refreshOnce).
// Exported so the bootstrap gate (components/AuthBootstrapGate) reuses the SAME single-flight
// on a hard reload instead of racing its own refresh.
let refreshPromise: Promise<string | null> | null = null;

export const refreshAccessToken = (staleToken?: string): Promise<string | null> => {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        if (typeof navigator !== 'undefined' && navigator.locks?.request) {
          return await navigator.locks.request('relif-auth-refresh', () => refreshOnce(staleToken));
        }
        // no Web Locks (old browser) → per-tab single-flight only. Worst case the
        // multi-tab race resurfaces; the widened server-side rotation grace still covers it.
        return await refreshOnce(staleToken);
      } finally {
        refreshPromise = null;
      }
    })();
  }
  return refreshPromise;
};

// Only 401s flagged by the auth middleware (X-Auth-Error: token-invalid) count as
// session-loss. Route-level 401s (defensive null checks, RBAC denials) must not nuke the
// session — they should surface as errors while the user stays signed in. See
// apps/saas_back/src/middlewares/auth.ts:throwTokenError.
const isTokenError = (result: { error?: unknown; meta?: { response?: Response } }): boolean => {
  const error = result.error as { status?: number | string; originalStatus?: number } | undefined;
  const isUnauthorized = error?.status === 401 || (error?.status === 'PARSING_ERROR' && error?.originalStatus === 401);
  const authErrorHeader = result.meta?.response?.headers.get('X-Auth-Error');
  return isUnauthorized && authErrorHeader === 'token-invalid';
};

const baseQueryWithAuthRedirect = async (
  args: Parameters<typeof baseQuery>[0],
  api: Parameters<typeof baseQuery>[1],
  extraOptions: Parameters<typeof baseQuery>[2]
) => {
  let result = await baseQuery(args, api, extraOptions);

  if (isTokenError(result)) {
    if (PATHS_WITH_SPECIAL_AUTH.includes(window.location.pathname)) {
      return result;
    }

    // The access token is short-lived by design — expiring is the normal case, not an
    // anomaly. Rotate it silently through the refresh cookie and replay the request;
    // only a dead refresh session falls through to the sign-in redirect. Pass the token
    // that just 401'd so a sibling tab's already-rotated token can be adopted instead.
    const staleToken = (api.getState() as { user?: { accessToken?: string } })?.user?.accessToken;
    const newToken = await refreshAccessToken(staleToken);
    if (newToken) {
      api.dispatch(setAccessToken(newToken));
      result = await baseQuery(args, api, extraOptions);
      if (!isTokenError(result)) {
        return result;
      }
    }

    api.dispatch(signOutUser());
    clearPersistedUser();

    // Avoid redirect loops
    if (window.location.pathname !== '/sign-in') {
      const redirect = encodeURIComponent(`${window.location.pathname}${window.location.search}`);
      window.location.assign(`/sign-in?redirect=${redirect}`);
    }
  }
  return result;
};

const baseApi = createApi({
  baseQuery: baseQueryWithAuthRedirect,
  tagTypes: [
    'BusinessUnit',
    'BusinessUnitPromptVersions',
    'BusinessUnitTemplates',
    'BusinessUnitHolidays',
    'FollowUpConfig',
    'Reports',
    'Leads',
    'Surveys',
    'Metrics',
    'Client',
    'Clients',
    'Tickets',
    'Ticket',
    'Sales',
    'Documents',
    'MetaConnection',
    'Appointments',
    'CurrentUser',
    'QuickReplies',
    'WhatsAppHealth',
  ],
  endpoints: () => ({}),
});

export default baseApi;
